import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import { defaultRequestTransformer } from '@/utils/api';
import { accountMetadataPath } from '@/utils/monolithApiRouteService';

export type UpdateUserMetadataRequest = Record<string, null | undefined | string | number | string[] | boolean>;

type RetryPolicy = boolean | number | ((failureCount: number, error: Error) => boolean);

interface UseMetadataOptions {
  enabled?: boolean;
  onError?: () => void;
  onSuccess?: () => void;
  transform?: boolean;
  retry?: RetryPolicy;
  survivesNavigation?: boolean;
}

export const useUserMetadata = <T>(key: string, opts: UseMetadataOptions) => {
  const { data, ...rest } = useMonolithApiQuery<{ value: T }, unknown>(['usermetadata', key], accountMetadataPath(), {
    requestConfig: { params: { key } },
    ...opts,
  });

  return {
    data: data?.value,
    ...rest,
  };
};

const noTransformConfig = {
  transformRequest: defaultRequestTransformer,
};

// `keepalive` hands the request to the browser, which finishes it even if the page unloads a moment
// later. It is a fetch option, so it needs the fetch adapter: axios defaults to XHR in the browser,
// and the unload cancels an in-flight XHR. fetch resolves no relative URL of its own, hence the
// same-origin baseURL that XHR would otherwise supply.
const survivesNavigationConfig = {
  adapter: 'fetch' as const,
  fetchOptions: { keepalive: true },
  baseURL: window.location.origin,
};

export const useMutateUserMetadata = (opts: UseMetadataOptions = {}) => {
  const { onSuccess, onError, transform = true, retry, survivesNavigation } = opts;

  return useMonolithApiMutation<unknown, unknown, UpdateUserMetadataRequest>(accountMetadataPath(), 'put', {
    retry,
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: () => {
      if (onError) {
        onError();
      }
    },
    requestConfig: {
      ...(transform ? undefined : noTransformConfig),
      ...(survivesNavigation ? survivesNavigationConfig : undefined),
    },
  });
};
